// src/components/dashboard/profile.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  getProfile,
  updateEmail,
  changePassword,
  updateUsername,
} from "../../services/profileApi";
import {
  fetchPublicContact,
  updatePublicContact,
} from "../../services/siteApi";
import { useAuth } from "../../context/AuthContext";

/** Small, reusable error banner */
function ErrorBanner({ message, requestId, onRetry }) {
  if (!message) return null;
  return (
    <div
      className="alert alert-danger d-flex align-items-start gap-2"
      role="alert"
      aria-live="assertive"
    >
      <div className="flex-grow-1">
        <strong>Oops!</strong> {message}
        {requestId ? (
          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
            Support code: <code>{requestId}</code>
          </div>
        ) : null}
      </div>
      {onRetry && (
        <button
          type="button"
          className="btn btn-outline-light btn-sm ms-2"
          onClick={onRetry}
        >
          Retry
        </button>
      )}
    </div>
  );
}

/** Inline field error */
function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div className="invalid-feedback d-block" role="alert">
      {msg}
    </div>
  );
}

const Profile = () => {
  const { checkAuth } = useAuth();

  // server profile (admin account)
  const [profile, setProfile] = useState({
    username: "",
    email: "",
  });

  // site public contact
  const [siteContact, setSiteContact] = useState({
    label_email: "Email",
    email: "",
    label_phone: "WhatsApp",
    phone: "",
  });

  // global load states
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [loadSupportCode, setLoadSupportCode] = useState("");

  // Username form
  const [username, setUsername] = useState("");
  const [usernameMsg, setUsernameMsg] = useState("");
  const [usernameErr, setUsernameErr] = useState("");
  const [savingUsername, setSavingUsername] = useState(false);

  // Account email (login) form
  const [email, setEmail] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  // Password form
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwdMsg, setPwdMsg] = useState("");
  const [pwdErr, setPwdErr] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  // Site contact form
  const [scMsg, setScMsg] = useState("");
  const [scErr, setScErr] = useState("");
  const [savingSC, setSavingSC] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    setLoadSupportCode("");
    try {
      // 1) Admin account profile (requires auth)
      const me = await getProfile();
      if (!me) throw new Error("Empty profile payload");
      setProfile({
        username: me.username ?? "",
        email: me.email ?? "",
      });
      setUsername(me.username ?? "");
      setEmail(me.email ?? "");

      // 2) Public contact (no auth)
      const c = await fetchPublicContact();
      if (c) {
        setSiteContact({
          label_email: c.label_email ?? "Email",
          email: c.email ?? "",
          label_phone: c.label_phone ?? "WhatsApp",
          phone: c.phone ?? "",
        });
      }
    } catch (err) {
      console.error("[Profile] load error:", err);
      setLoadError(err?.ui?.message || "Could not load your settings.");
      setLoadSupportCode(err?.ui?.requestId || "");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // --- Handlers: Admin account ---
  const onUpdateUsername = async (e) => {
    e.preventDefault();
    setUsernameMsg("");
    setUsernameErr("");
    setSavingUsername(true);
    try {
      const res = await updateUsername(username.trim());
      const usr = res?.user;
      setProfile((p) => ({ ...p, username: usr?.username ?? username }));
      setUsernameMsg(res?.message || "Username updated");
      await checkAuth();
    } catch (err) {
      console.error("[Profile] username update error:", err);
      const fieldMsg =
        err?.ui?.fieldErrors?.username?.[0] ||
        err?.ui?.message ||
        "Failed to update username";
      setUsernameErr(fieldMsg);
    } finally {
      setSavingUsername(false);
    }
  };

  const onUpdateEmail = async (e) => {
    e.preventDefault();
    setEmailMsg("");
    setEmailErr("");
    setSavingEmail(true);
    try {
      const { user, message } = await updateEmail(email.trim());
      setProfile((p) => ({ ...p, email: user?.email ?? email }));
      setEmailMsg(message || "Login email updated");
      await checkAuth();
    } catch (err) {
      console.error("[Profile] email update error:", err);
      const fieldMsg =
        err?.ui?.fieldErrors?.email?.[0] ||
        err?.ui?.message ||
        "Failed to update login email";
      setEmailErr(fieldMsg);
    } finally {
      setSavingEmail(false);
    }
  };

  const onChangePassword = async (e) => {
    e.preventDefault();
    setPwdMsg("");
    setPwdErr("");

    if (newPassword !== confirmPassword) {
      setPwdErr("New password and confirm do not match");
      return;
    }

    setSavingPwd(true);
    try {
      const res = await changePassword({ currentPassword, newPassword });
      setPwdMsg(res?.message || "Password updated");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("[Profile] password update error:", err);
      const fieldMsg =
        err?.ui?.fieldErrors?.currentPassword?.[0] ||
        err?.ui?.fieldErrors?.newPassword?.[0] ||
        err?.ui?.message ||
        "Failed to change password";
      setPwdErr(fieldMsg);
    } finally {
      setSavingPwd(false);
    }
  };

  // --- Handler: Public contact (site-wide) ---
  const onSaveSiteContact = async (e) => {
    e.preventDefault();
    setScMsg("");
    setScErr("");
    setSavingSC(true);
    try {
      const payload = {
        label_email: siteContact.label_email?.trim() || "Email",
        email: siteContact.email?.trim(),
        label_phone: siteContact.label_phone?.trim() || "WhatsApp",
        phone: siteContact.phone?.trim(),
      };
      if (!payload.email || !payload.phone) {
        const error = new Error("Email and phone are required.");
        error.ui = { message: "Email and phone are required." };
        throw error;
      }

      const res = await updatePublicContact(payload); // requires admin auth (cookie)
      setScMsg(res?.message || "Public contact updated");
    } catch (err) {
      console.error("[Profile] site contact update error:", err);
      setScErr(
        err?.ui?.message ||
          err?.response?.data?.error ||
          "Failed to update public contact"
      );
    } finally {
      setSavingSC(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4" style={{ maxWidth: 800 }}>
        <div className="placeholder-glow">
          <div className="placeholder col-4 mb-3"></div>
          <div className="card p-3 mb-4">
            <span className="placeholder col-6 mb-2"></span>
            <span className="placeholder col-8 mb-2"></span>
            <span className="placeholder col-5"></span>
          </div>
          <div className="card p-3 mb-4">
            <span className="placeholder col-7 mb-2"></span>
            <span className="placeholder col-4"></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4" style={{ maxWidth: 800 }}>
      <h2 className="mb-3">Admin Settings</h2>
      <p className="text-muted mb-4">
        Manage your <strong>account</strong> (used for logging in) and the{" "}
        <strong>public contact details</strong> shown on your website.
      </p>

      {/* Global load error (with retry + support code) */}
      <ErrorBanner
        message={loadError}
        requestId={loadSupportCode}
        onRetry={loadError ? load : undefined}
      />

      {/* Account info */}
      <div className="card p-3 mb-4">
        <h5 className="mb-2">Account Information (Login)</h5>
        <p className="mb-1">
          <strong>Username:</strong> {profile.username || "—"}
        </p>
        <p className="mb-0">
          <strong>Login Email:</strong> {profile.email || "—"}
        </p>
      </div>

      {/* Change Username */}
      <form onSubmit={onUpdateUsername} className="card p-3 mb-4">
        <h5 className="mb-1">Change Username</h5>
        <p className="text-muted mb-3">
          This changes the <em>login username</em> for the admin account.
        </p>
        <input
          type="text"
          className={`form-control mb-2 ${usernameErr ? "is-invalid" : ""}`}
          placeholder="Enter new username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          disabled={savingUsername}
        />
        <FieldError msg={usernameErr} />
        <button className="btn btn-primary" disabled={savingUsername}>
          {savingUsername ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Updating…
            </>
          ) : (
            "Update Username"
          )}
        </button>
        {usernameMsg && (
          <div className="alert alert-success mt-2 mb-0">{usernameMsg}</div>
        )}
      </form>

      {/* Change Account Email */}
      <form onSubmit={onUpdateEmail} className="card p-3 mb-4">
        <h5 className="mb-1">Change Login Email</h5>
        <p className="text-muted mb-3">
          This updates the <em>admin account email</em> used for signing in. It
          does not change the public email shown on the website.
        </p>
        <input
          type="email"
          className={`form-control mb-2 ${emailErr ? "is-invalid" : ""}`}
          placeholder="Enter new login email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={savingEmail}
        />
        <FieldError msg={emailErr} />
        <button className="btn btn-primary" disabled={savingEmail}>
          {savingEmail ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Updating…
            </>
          ) : (
            "Update Login Email"
          )}
        </button>
        {emailMsg && (
          <div className="alert alert-success mt-2 mb-0">{emailMsg}</div>
        )}
      </form>

      {/* Change Password */}
      <form onSubmit={onChangePassword} className="card p-3 mb-4">
        <h5 className="mb-1">Change Password</h5>
        <p className="text-muted mb-3">
          Use a strong password. You’ll need the current password to set a new
          one.
        </p>
        <input
          type="password"
          className={`form-control mb-2 ${pwdErr ? "is-invalid" : ""}`}
          placeholder="Current password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          disabled={savingPwd}
        />
        <input
          type="password"
          className={`form-control mb-2 ${pwdErr ? "is-invalid" : ""}`}
          placeholder="New password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          disabled={savingPwd}
        />
        <input
          type="password"
          className={`form-control mb-2 ${pwdErr ? "is-invalid" : ""}`}
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={savingPwd}
        />
        <FieldError msg={pwdErr} />
        <button className="btn btn-secondary" disabled={savingPwd}>
          {savingPwd ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Changing…
            </>
          ) : (
            "Change Password"
          )}
        </button>
        {pwdMsg && (
          <div className="alert alert-success mt-2 mb-0">{pwdMsg}</div>
        )}
      </form>

      {/* Public Contact Settings */}
      <form onSubmit={onSaveSiteContact} className="card p-3 mb-5">
        <h5 className="mb-1">Public Contact on Website</h5>
        <p className="text-muted mb-3">
          These details appear on your homepage “Contact” section and are
          visible to all visitors.
        </p>

        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Email Label (title)</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g., Email (Afra)"
              value={siteContact.label_email}
              onChange={(e) =>
                setSiteContact((s) => ({ ...s, label_email: e.target.value }))
              }
            />
            <div className="form-text">
              Shown as the title next to the public email (e.g., “Email
              (Afra)”).
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">Public Email</label>
            <input
              type="email"
              className="form-control"
              placeholder="contact@example.com"
              value={siteContact.email}
              onChange={(e) =>
                setSiteContact((s) => ({ ...s, email: e.target.value }))
              }
              required
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Phone/WhatsApp Label (title)</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g., WhatsApp (Afra)"
              value={siteContact.label_phone}
              onChange={(e) =>
                setSiteContact((s) => ({ ...s, label_phone: e.target.value }))
              }
            />
            <div className="form-text">
              Shown as the title next to the public phone (e.g., “WhatsApp
              (Afra)”).
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">Public Phone / WhatsApp</label>
            <input
              type="tel"
              className="form-control"
              placeholder="0777777100"
              value={siteContact.phone}
              onChange={(e) =>
                setSiteContact((s) => ({ ...s, phone: e.target.value }))
              }
              required
            />
            <div className="form-text">
              This is the number visitors see (used for the WhatsApp link).
            </div>
          </div>
        </div>

        <FieldError msg={scErr} />
        <button className="btn btn-success mt-3" disabled={savingSC}>
          {savingSC ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Saving…
            </>
          ) : (
            "Save Public Contact"
          )}
        </button>
        {scMsg && <div className="alert alert-success mt-2 mb-0">{scMsg}</div>}
      </form>
    </div>
  );
};

export default Profile;
